import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Music, ListMusic, RefreshCw, Upload, ExternalLink, Search, X, DollarSign, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * RadioAdminTab — admin-only CRUD for stations / tracks / playlists.
 * Uses /api/admin/radio/* endpoints which are already live on the backend.
 * Three sub-views: Stations · Tracks · Playlists. Stations view has a
 * "+ Billboard Hot 100" quick-refresh button that reseeds the chart.
 */
export default function RadioAdminTab() {
  const [sub, setSub] = useState("stations");

  return (
    <div data-testid="radio-admin-tab" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-display font-black text-white flex items-center gap-2">
          <Music className="w-6 h-6 text-tv-accent" /> Radio Network
        </h2>
        <div className="flex gap-2">
          {[
            { k: "stations", label: "Stations" },
            { k: "tracks", label: "Tracks" },
            { k: "playlists", label: "Playlists" },
            { k: "ads", label: "Ad sales" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`radio-admin-sub-${t.k}`}
              onClick={() => setSub(t.k)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                sub === t.k ? "bg-tv-accent text-black" : "bg-white/10 text-white/70 hover:bg-white/20"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {sub === "stations" && <StationsPane />}
      {sub === "tracks" && <TracksPane />}
      {sub === "playlists" && <PlaylistsPane />}
      {sub === "ads" && <AdSalesPane />}
    </div>
  );
}

function StationsPane() {
  const [stations, setStations] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", category: "music", description: "", cover_url: "", order: 0, is_active: true });

  const load = async () => {
    try {
      const { data } = await api.get("/radio/stations");
      setStations(data || []);
    } catch { setStations([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/radio/stations", form);
      toast.success(`Station "${form.name}" created`);
      setForm({ slug: "", name: "", category: "music", description: "", cover_url: "", order: 0, is_active: true });
      setAdding(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const update = async (s, patch) => {
    try {
      await api.put(`/admin/radio/stations/${s.id}`, { ...s, ...patch });
      toast.success(`Saved "${s.name}"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}" and all its tracks?`)) return;
    try {
      await api.delete(`/admin/radio/stations/${s.id}`);
      toast.success(`Deleted "${s.name}"`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button
          data-testid="radio-admin-station-new"
          onClick={() => setAdding((v) => !v)}
          className="bg-tv-accent text-black font-bold text-sm px-4 py-2 rounded flex items-center gap-1.5"
        >
          <Plus className="w-4 h-4" /> {adding ? "Cancel" : "New Station"}
        </button>
      </div>

      {adding && (
        <form onSubmit={create} className="bg-white/5 border border-white/10 rounded-xl p-4 grid grid-cols-2 gap-3">
          <input required placeholder="Slug (unique, lowercase-hyphen)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input placeholder="Cover image URL" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <button type="submit" className="col-span-2 bg-tv-accent text-black font-bold text-sm py-2 rounded">Create Station</button>
        </form>
      )}

      <div className="divide-y divide-white/5 bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {stations.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-3 hover:bg-white/5" data-testid={`radio-admin-station-${s.slug}`}>
            <div
              className="w-12 h-12 rounded shrink-0 bg-white/10"
              style={{ backgroundImage: `url(${s.cover_url})`, backgroundSize: "cover", backgroundPosition: "center" }}
            />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm">{s.name}</div>
              <div className="text-white/50 text-xs">{s.slug} · {s.track_count || 0} tracks · order {s.order}</div>
            </div>
            <input
              type="number"
              defaultValue={s.order}
              onBlur={(e) => { const v = parseInt(e.target.value) || 0; if (v !== s.order) update(s, { order: v }); }}
              className="w-14 bg-black/40 border border-white/10 rounded px-2 py-1 text-white text-xs"
              title="Sort order"
            />
            <label className="flex items-center gap-1 text-[10px] text-white/60">
              <input type="checkbox" checked={s.is_active !== false} onChange={(e) => update(s, { is_active: e.target.checked })} className="accent-tv-accent" /> Active
            </label>
            <button data-testid={`radio-admin-station-delete-${s.slug}`} onClick={() => remove(s)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1.5 rounded">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {stations.length === 0 && <div className="p-4 text-white/50 text-sm">No stations yet.</div>}
      </div>
    </div>
  );
}

function TracksPane() {
  const [stations, setStations] = useState([]);
  const [stationSlug, setStationSlug] = useState("");
  const [tracks, setTracks] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => { api.get("/radio/stations").then((r) => { setStations(r.data || []); if (!stationSlug && r.data?.length) setStationSlug(r.data[0].slug); }); }, []);

  const loadTracks = async () => {
    if (!stationSlug) return;
    try {
      const { data } = await api.get(`/radio/stations/${stationSlug}`);
      setTracks(data?.tracks || []);
    } catch { setTracks([]); }
  };
  useEffect(() => { loadTracks(); }, [stationSlug]);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get(`/radio/search?q=${encodeURIComponent(q)}`);
        setResults(data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(id);
  }, [q]);

  const addFromHit = async (hit, full = false) => {
    try {
      await api.post(`/radio/stations/${stationSlug}/tracks`, {
        title: hit.title, artist: hit.artist, album: hit.album, cover_url: hit.cover_url,
        source_url: full ? hit.youtube_search_url : (hit.preview_url || hit.youtube_search_url),
        source_type: full ? "youtube" : "url",
        duration_sec: hit.duration_sec || 0,
      });
      toast.success(`+ ${hit.title}`);
      loadTracks();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Bulk-add every result currently shown — useful when a host has typed
  // an artist name and wants to import their whole catalogue at once.
  const addAllResults = async () => {
    if (!results.length) return;
    try {
      const payload = {
        tracks: results.map((hit) => ({
          title: hit.title,
          artist: hit.artist,
          album: hit.album,
          cover_url: hit.cover_url,
          source_url: hit.preview_url || hit.youtube_search_url,
          source_type: hit.preview_url ? "url" : "youtube",
          duration_sec: hit.duration_sec || 0,
        })),
      };
      const { data } = await api.post(`/radio/stations/${stationSlug}/tracks/bulk`, payload);
      toast.success(`Added ${data.added} track${data.added === 1 ? "" : "s"}${data.skipped_dupes ? ` · skipped ${data.skipped_dupes} dupe${data.skipped_dupes === 1 ? "" : "s"}` : ""}`);
      loadTracks();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/radio/tracks/${t.id}`);
      toast.success("Removed");
      loadTracks();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // SoundCloud full-length DJ-mix import — paste any soundcloud.com URL.
  const [scUrl, setScUrl] = useState("");
  const [scImporting, setScImporting] = useState(false);
  const importSoundcloudMix = async () => {
    if (!scUrl.trim()) return;
    setScImporting(true);
    try {
      const { data } = await api.post(`/radio/stations/${stationSlug}/import-soundcloud`, { url: scUrl.trim() });
      if (data?.duplicate) {
        toast.info("That track is already in the station");
      } else {
        const mins = Math.round((data?.duration_sec || 0) / 60);
        toast.success(`+ ${data?.title || "Track"} ${mins ? `(${mins} min DJ mix)` : ""}`);
      }
      setScUrl("");
      loadTracks();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScImporting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select
          data-testid="radio-admin-track-station"
          value={stationSlug}
          onChange={(e) => setStationSlug(e.target.value)}
          className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm"
        >
          {stations.map((s) => <option key={s.id} value={s.slug}>{`${s.name} (${s.track_count || 0})`}</option>)}
        </select>
        <div className="relative flex-1 min-w-[240px]">
          <input
            data-testid="radio-admin-track-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Bulk add — type artist or song title…"
            className="w-full bg-black/40 border border-white/10 rounded pl-9 pr-3 py-2 text-white text-sm focus:border-tv-accent outline-none"
          />
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          {searching && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-tv-accent text-xs">…</div>}
        </div>
      </div>

      {results.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">{results.length} matching tracks</div>
            <button
              data-testid="radio-admin-bulk-add-btn"
              onClick={addAllResults}
              className="bg-emerald-500 hover:bg-emerald-400 text-black text-xs font-black px-3 py-1.5 rounded-full"
            >
              + Add all {results.length} as a playlist
            </button>
          </div>
          <div className="bg-black/30 border border-white/10 rounded-xl p-2 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto" data-testid="radio-admin-track-search-results">
            {results.map((hit, i) => (
              <div key={i} className="flex items-center gap-2 bg-white/5 rounded p-1.5">
                <div className="w-10 h-10 rounded bg-white/10 shrink-0" style={{ backgroundImage: `url(${hit.cover_url})`, backgroundSize: "cover" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-white text-xs font-bold line-clamp-1">{hit.title}</div>
                  <div className="text-white/50 text-[10px] line-clamp-1">{hit.artist} · {hit.album}</div>
                </div>
                <button onClick={() => addFromHit(hit, false)} className="bg-tv-accent text-black text-[10px] font-black px-2 py-1 rounded">+ Preview</button>
                <button onClick={() => addFromHit(hit, true)} className="bg-white/10 text-white text-[10px] font-black px-2 py-1 rounded">+ Full</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SoundCloud DJ Mix import — paste a soundcloud.com URL to add a
          full-length set as a single track. Different from the iTunes
          search above which adds short songs only. */}
      <div className="bg-black/30 border border-orange-500/20 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="radio-admin-soundcloud-import">
        <span className="text-[10px] uppercase tracking-wider text-orange-300 font-bold">SoundCloud DJ Mix</span>
        <input
          data-testid="radio-admin-soundcloud-url"
          value={scUrl}
          onChange={(e) => setScUrl(e.target.value)}
          placeholder="Paste a SoundCloud track or DJ-mix URL…"
          className="flex-1 min-w-[220px] bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-orange-400 outline-none"
        />
        <button
          data-testid="radio-admin-soundcloud-import-btn"
          onClick={importSoundcloudMix}
          disabled={scImporting || !scUrl.trim()}
          className="bg-orange-500 hover:bg-orange-400 disabled:opacity-40 text-black text-xs font-black px-3 py-2 rounded-full"
        >
          {scImporting ? "Importing…" : "+ Import full mix"}
        </button>
      </div>

      <div className="divide-y divide-white/5 bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="radio-admin-track-list">
        {tracks.map((t, i) => (
          <div key={t.id} className="flex items-center gap-2 p-2 hover:bg-white/5">
            <div className="w-6 text-center text-white/40 text-xs font-black tabular-nums">{i + 1}</div>
            <div className="w-10 h-10 rounded bg-white/10 shrink-0" style={{ backgroundImage: `url(${t.cover_url})`, backgroundSize: "cover" }} />
            <div className="flex-1 min-w-0">
              <div className="text-white text-xs font-bold line-clamp-1">{t.title}</div>
              <div className="text-white/50 text-[10px] line-clamp-1">{t.artist} · {t.source_type} · {t.plays_count || 0} plays</div>
            </div>
            <button onClick={() => remove(t)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1.5 rounded" data-testid={`radio-admin-track-delete-${t.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {tracks.length === 0 && <div className="p-4 text-white/50 text-sm">Empty station.</div>}
      </div>
    </div>
  );
}

function PlaylistsPane() {
  const [playlists, setPlaylists] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", description: "", cover_url: "", kind: "curated", is_featured: true, track_ids: [] });

  const load = async () => {
    try {
      const { data } = await api.get("/radio/playlists");
      setPlaylists(data || []);
    } catch { setPlaylists([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/radio/playlists", form);
      toast.success(`Playlist "${form.name}" created`);
      setForm({ slug: "", name: "", description: "", cover_url: "", kind: "curated", is_featured: true, track_ids: [] });
      setAdding(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete playlist "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/radio/playlists/${p.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <button onClick={() => setAdding((v) => !v)} className="bg-tv-accent text-black font-bold text-sm px-4 py-2 rounded flex items-center gap-1.5" data-testid="radio-admin-playlist-new">
          <Plus className="w-4 h-4" /> {adding ? "Cancel" : "New Playlist"}
        </button>
      </div>
      {adding && (
        <form onSubmit={create} className="bg-white/5 border border-white/10 rounded-xl p-4 grid grid-cols-2 gap-3">
          <input required placeholder="Slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <input placeholder="Cover image URL" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} className="col-span-2 bg-black/40 border border-white/10 rounded px-3 py-2 text-white text-sm" />
          <label className="flex items-center gap-2 text-white text-sm">
            <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} className="accent-tv-accent" /> Featured on /radio
          </label>
          <button type="submit" className="bg-tv-accent text-black font-bold text-sm py-2 rounded">Create</button>
        </form>
      )}
      <div className="divide-y divide-white/5 bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {playlists.map((p) => (
          <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`radio-admin-playlist-${p.slug}`}>
            <div className="w-12 h-12 rounded shrink-0 bg-white/10" style={{ backgroundImage: `url(${p.cover_url})`, backgroundSize: "cover" }} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm">{p.name}</div>
              <div className="text-white/50 text-xs">{p.slug} · {p.track_ids?.length || 0} tracks · {p.kind}</div>
            </div>
            <a href={`/radio/playlist/${p.slug}`} target="_blank" rel="noreferrer" className="bg-white/5 hover:bg-white/10 text-white p-1.5 rounded" title="Open on site">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button onClick={() => remove(p)} className="bg-red-500/20 hover:bg-red-500/40 text-red-200 p-1.5 rounded">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {playlists.length === 0 && <div className="p-4 text-white/50 text-sm">No playlists yet.</div>}
      </div>
    </div>
  );
}


// ── Ad sales approval pane ──────────────────────────────────────────────
function AdSalesPane() {
  const [status, setStatus] = useState("requested");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/radio/ads/bookings`, { params: { status } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const decide = async (b, action) => {
    try {
      await api.post(`/admin/radio/ads/bookings/${b.id}/${action}`, { note: notes[b.id] || "" });
      toast.success(action === "approve" ? "Booking approved — live rotation updated" : "Booking denied");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3" data-testid="admin-radio-ads-pane">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {["requested", "approved", "live", "completed", "denied", "all"].map((s) => (
            <button
              key={s}
              data-testid={`admin-radio-ads-filter-${s}`}
              onClick={() => setStatus(s)}
              className={`text-xs font-bold px-3 py-1.5 rounded ${status === s ? "bg-tv-accent text-black" : "bg-white/5 text-white/70"}`}
            >{s}</button>
          ))}
        </div>
        <button onClick={load} className="ml-auto text-white/60 hover:text-white text-sm inline-flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {loading ? <div className="text-white/60">Loading…</div> : (
        <div className="space-y-2">
          {rows.length === 0 && <div className="text-white/40 text-sm text-center py-6">No {status} bookings.</div>}
          {rows.map((b) => (
            <div key={b.id} data-testid={`admin-radio-ad-${b.id}`} className="bg-white/5 border border-white/10 rounded p-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-black text-base flex-1 truncate">
                  <DollarSign className="w-4 h-4 inline text-emerald-400" /> {b.advertiser_name}
                </div>
                <span className="text-emerald-300 font-black">${Number(b.rate_usd || 0).toFixed(2)}</span>
                <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded ${
                  b.status === "requested" ? "bg-amber-500/25 text-amber-200" :
                  b.status === "approved" ? "bg-sky-500/25 text-sky-200" :
                  b.status === "live" ? "bg-emerald-500/25 text-emerald-200" :
                  b.status === "denied" ? "bg-red-500/25 text-red-200" : "bg-white/10 text-white/60"
                }`}>{b.status}</span>
              </div>
              <div className="text-white/70 text-sm mt-0.5">{b.contact_email} · station <code>{b.station_slug}</code> · rotation every {b.rotation_min} min</div>
              <div className="text-white/60 text-xs mt-0.5">{new Date(b.start_at).toLocaleString()} → {new Date(b.end_at).toLocaleString()}</div>
              {b.notes && <div className="text-white/50 text-xs italic mt-1">"{b.notes}"</div>}
              {b.decision_note && <div className="text-white/50 text-xs mt-1">Decision note: "{b.decision_note}"</div>}
              {b.status === "requested" && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <input
                    data-testid={`admin-ad-note-${b.id}`}
                    value={notes[b.id] || ""}
                    onChange={(e) => setNotes({ ...notes, [b.id]: e.target.value })}
                    placeholder="Decision note (optional)"
                    className="flex-1 min-w-[180px] bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white"
                  />
                  <button
                    data-testid={`admin-ad-approve-${b.id}`}
                    onClick={() => decide(b, "approve")}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
                  ><CheckCircle className="w-3 h-3" /> Approve</button>
                  <button
                    data-testid={`admin-ad-deny-${b.id}`}
                    onClick={() => decide(b, "deny")}
                    className="bg-red-500/20 hover:bg-red-500/40 text-red-200 font-black text-xs px-3 py-1.5 rounded inline-flex items-center gap-1"
                  ><XCircle className="w-3 h-3" /> Deny</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
